import React, { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const formatError = (detail) => {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
};

const Login = () => {
  const { user, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("admin@bagfluence.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    const from = location.state?.from?.pathname || "/app";
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-radial-blue pointer-events-none" />
      <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />

      <Link
        to="/"
        className="absolute top-6 left-6 text-sm text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1.5"
        data-testid="login-back-link"
      >
        <ArrowLeft className="h-4 w-4" /> Back to site
      </Link>

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2 mb-8" data-testid="login-logo">
          <Logo className="h-11 w-auto" />
        </div>

        <div className="rounded-2xl border border-white/8 bg-zinc-950/70 backdrop-blur-xl p-8">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Sign in</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Access the Bagfluence operating system.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100"
                data-testid="login-input-email"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100"
                placeholder="bagfluence123"
                data-testid="login-input-password"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11 glow-blue"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <div className="mt-6 rounded-lg border border-white/8 bg-white/[0.02] p-3 text-xs text-zinc-400">
            <span className="text-zinc-500">Demo:</span>{" "}
            <span className="text-zinc-200">admin@bagfluence.com</span> /{" "}
            <span className="text-zinc-200">bagfluence123</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
